/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from 'react';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import AssetConfiguratorComponent from './AssetConfiguratorComponent';
import { collection } from 'utils/immutable';
import { setAssetInEdit, setAssetDirty, changeAssetProperty } from 'features/offer/edit/assets/asset/actionCreators';
import { downloadAssetImage } from 'features/asset/image/actionCreators';
import { getEquipments, getHierarchyEquipmentsId } from '../selectors.js';
import { RightsContext } from 'providers/RightsContext';
import { withParams } from 'providers/ParamsConsumer';
import { checkHierarchyTriggers, loadHierarchyLevels } from '../actionCreators';
import { getAssetHierarchyImage } from '../../hierarchy/selectors';
import { applyHierarchyRules, applyHierarchyValidationRules, hierarchyIsValid } from '../../hierarchy/rules';
import curry from 'lodash.curry';
import { round } from '../../../offer/edit/assets/asset/assetFunctions';
import { getDateValue } from 'utils/functions';

const AssetConfiguratorContainer = ({
  actions,
  onCloseModal,
  vatPercent,
  showConfigurator,
  assetTypes,
  params,
  asset,
  hierarchy,
  data,
  equipmentsCollection,
  editRight,
  images
}) => {
  const [hierarchyLevels, setHierarchyLevels] = useState(asset.hierarchy);
  const [assetImage, setAssetImage] = useState(getAssetHierarchyImage(hierarchyLevels, images));
  const [assetInfo, setAssetInfo] = useState({
    assetTypeId: asset.assetTypeId,
    isSecondHand: asset.isSecondHand,
    buildYearAndMonth: asset.buildYearAndMonth
  });

  const getSelectedAssetEquipments = () => {
    const selectedOptions = asset.equipments
      ? asset.equipments.map(x => ({
          id: x.id,
          code: x.code,
          name: x.name,
          price: x.price,
          typeName: x.typeName
        }))
      : [];
    return selectedOptions;
  };

  const getAssetEquipments = () => {
    const selectedOptions = getSelectedAssetEquipments();
    const assetEquipments = {
      basePrice: data.basePrice,
      finalPrice: data.basePrice + selectedOptions.map(x => x.price).reduce((a, b) => a + b, 0),
      selectedOptions
    };

    return assetEquipments;
  };

  const getAvailableAssetEquipments = () => {
    const selectedOptions = getSelectedAssetEquipments();
    const equipmentsId = getHierarchyEquipmentsId(hierarchyLevels);
    const { equipments, loaded } = getEquipments(equipmentsCollection, equipmentsId);

    const availableOptions = equipments.map(z => ({
      ...z,
      selected: selectedOptions.find(y => y.name === z.name) ? true : false
    }));

    return { availableOptions, availableOptionsLoaded: loaded };
  };

  const { availableOptions, availableOptionsLoaded } = getAvailableAssetEquipments();
  const [equipments, setEquipments] = useState(getAssetEquipments());
  const [isDirty, setIsDirty] = useState(false);
  const [nameFilter, setNameFilter] = useState('');
  const [options, setOptions] = useState(availableOptions);
  const [paginationObj, setPagination] = useState({ page: 1, pageSize: 5, totalCount: 0 });
  const [optionsPagination, setOptionsPagination] = useState([]);

  const { financedAssetWrite } = useContext(RightsContext);
  const { assetHierarchyConfiguration, defaultFabricationMaxMonths } = params;

  const enabledAssetTypeCodes = assetHierarchyConfiguration.filter(x => x.enableConfiguratorYN).map(a => a.hierarchyCode);
  const filteredAssetTypes = assetTypes.filter(item => {
    return enabledAssetTypeCodes.indexOf(item.code) !== -1;
  });

  useEffect(() => {
    if (availableOptionsLoaded) {
      if (equipments.selectedOptions.length === 0) {
        setOptions(availableOptions.map(x => ({ ...x, selected: false })));
        setPagination({ ...paginationObj, totalCount: availableOptions.length });
        setOptionsPagination(pagination(availableOptions, paginationObj.pageSize, paginationObj.page));
      } else {
        setOptions(availableOptions);
        setPagination({ ...paginationObj, totalCount: availableOptions.length });
        setOptionsPagination(pagination(availableOptions, paginationObj.pageSize, paginationObj.page));
      }
    }
  }, [availableOptionsLoaded]);

  useEffect(() => {
    setEquipments({
      ...equipments,
      basePrice: data.basePrice,
      finalPrice: data.basePrice + equipments.selectedOptions.map(x => x.price).reduce((a, b) => a + b, 0)
    });
  }, [data.basePrice]);

  useEffect(() => {
    if (!hierarchyLevels || hierarchyLevels.loaded === undefined || hierarchyLevels.loaded) return;

    actions.loadHierarchyLevels(assetInfo.assetTypeId, editRight).then(levels => {
      const newLevels = applyHierarchyValidationRules(levels);
      setHierarchyLevels(newLevels);
    });
  }, [hierarchyLevels]);

  useEffect(() => {
    setAssetImage(getAssetHierarchyImage(hierarchyLevels, images));
  }, [images]);

  const resetHierarchyLevels = () => {
    const levels = Object.assign([], { loaded: false });
    setHierarchyLevels(levels);
  };

  const onFilterChange = value => {
    setNameFilter(value);
    const filteredOptions = options.filter(x => x.name.includes(value));
    setPagination({ ...paginationObj, totalCount: filteredOptions.length });
    setOptionsPagination(pagination(filteredOptions, paginationObj.pageSize, paginationObj.page));
  };

  const onSelectedChange = (value, id) => {
    const newOptions = options.map(x => ({ ...x, selected: x.id === id ? value : x.selected }));
    setOptions(newOptions);

    setSelectedEquipments(value, id);

    setIsDirty(true);
    setOptionsPagination(pagination(newOptions, paginationObj.pageSize, paginationObj.page));
  };

  const setSelectedEquipments = (value, id) => {
    if (value === true) {
      const selectedOption = options.find(x => x.id === id);
      if (!selectedOption) return;
      setEquipments({
        ...equipments,
        selectedOptions: collection.insert(
          equipments.selectedOptions.length > 0 ? equipments.selectedOptions.length - 1 : 0,
          selectedOption,
          equipments.selectedOptions
        ),
        finalPrice: equipments.finalPrice + selectedOption.price
      });
    } else {
      const idx = equipments.selectedOptions.findIndex(x => x.id === id);
      if (idx === -1) return;
      const selectedOption = equipments.selectedOptions[idx];
      const finalPrice = equipments.finalPrice - selectedOption.price;
      setEquipments({
        ...equipments,
        finalPrice: finalPrice,
        selectedOptions: collection.remove(idx, equipments.selectedOptions)
      });
    }
  };

  const onPropertyChange = curry((prop, value) => {
    switch (prop) {
      case 'assetTypeId': {
        resetHierarchyLevels();

        setAssetInfo({
          ...assetInfo,
          assetTypeId: value.assetTypeId
        });
        break;
      }
      case 'hierarchyLevel': {
        const newHierarchyLevels = applyHierarchyRules(hierarchyLevels, value);
        setHierarchyLevels(newHierarchyLevels);
        actions.checkHierarchyTriggers(newHierarchyLevels);
        setAssetInfo({
          ...assetInfo,
          itemId: value
        });
        break;
      }
      case 'isSecondHand': {
        setAssetInfo({
          ...assetInfo,
          isSecondHand: value
        });

        break;
      }
      case 'buildYearAndMonth': {
        setAssetInfo({
          ...assetInfo,
          buildYearAndMonth: value
        });
        break;
      }
      default: {
        setEquipments({
          ...equipments,
          [prop]: value
        });
        break;
      }
    }

    setIsDirty(true);
  });

  const onSave = () => {
    if (!hierarchyIsValid(hierarchyLevels)) return;

    actions.setAssetInEdit(
      {
        hierarchy: hierarchyLevels,
        equipments: equipments.selectedOptions,
        isSecondHand: assetInfo.isSecondHand,
        assetTypeId: assetInfo.assetTypeId,
        buildYearAndMonth: getDateValue(assetInfo.buildYearAndMonth),
        itemId: assetInfo.itemId,
        acquisitionPriceWithoutVat: equipments.finalPrice,
        acquisitionPriceWithVat: round(2)(equipments.finalPrice * (1 + vatPercent / 100))
      },
      editRight
    );

    actions.setAssetDirty();

    setIsDirty(false);

    onCloseModal();
  };

  const pagination = (a, b, n) => {
    let current = (n - 1) * b;
    const end = current + b;
    const items = a.slice(current, end);
    return items;
  };

  const updatePageSize = (items, paginationObj) => {
    const page = paginationObj.page;
    const pageSize = paginationObj.pageSize;
    setOptionsPagination(pagination(items, pageSize, page));
  };

  const onChangePage = page => {
    const pageNo = ++page;
    updatePageSize(options, { ...paginationObj, page: pageNo });
    setPagination({ ...paginationObj, page: pageNo });
  };

  return (
    <AssetConfiguratorComponent
      vehicleEquipments={equipments}
      onCloseModal={onCloseModal}
      availableOptions={optionsPagination}
      onSelectedChange={onSelectedChange}
      assetTypes={filteredAssetTypes}
      assetInfo={assetInfo}
      assetImage={assetImage}
      maxMonths={defaultFabricationMaxMonths}
      onPropertyChange={onPropertyChange}
      onSave={onSave}
      showModal={showConfigurator}
      onFilterChange={onFilterChange}
      nameFilter={nameFilter}
      isDirty={isDirty}
      onChangePage={onChangePage}
      page={paginationObj.page}
      pageSize={paginationObj.pageSize}
      totalCount={paginationObj.totalCount}
      loading={!availableOptionsLoaded}
      financedAssetWrite={financedAssetWrite}
      hierarchyLevels={hierarchyLevels}
      hierarchy={hierarchy}
    />
  );
};

AssetConfiguratorContainer.propTypes = {
  assetTypes: PropTypes.array.isRequired,
  images: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  vatPercent: PropTypes.number,
  showConfigurator: PropTypes.bool.isRequired,
  asset: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  hierarchy: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  equipmentsCollection: PropTypes.object.isRequired,
  editRight: PropTypes.bool.isRequired
};

function mapStateToProps(state) {
  const { vatPercent } = state.offerConfig;
  const { hierarchy, data, equipments } = state.asset.configurator;
  return {
    vatPercent,
    assetTypes: state.assetData.assetTypes,
    images: state.assetData.images,
    hierarchy,
    data,
    equipmentsCollection: equipments
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        setAssetInEdit,
        setAssetDirty,
        downloadAssetImage,
        checkHierarchyTriggers,
        loadHierarchyLevels,
        changeAssetProperty
      },
      dispatch
    ),
    dispatch
  };
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(withParams(AssetConfiguratorContainer));
